/**
 * قارئ xlsx في المتصفح — **بلا أي مكتبة خارجية**.
 *
 * 🔴 **ليه في المتصفح أصلًا:** قاعدة مقفولة في السيستم — مفيش تحليل xlsx
 *    على السيرفر (سطح هجوم: zip متفجّر · كيانات XML خارجية · استنزاف
 *    ذاكرة)، و`openpyxl` مش متثبّتة في صورة الإنتاج. والمتصفح **هو** جهاز
 *    الموظف، فالقراءة عنده بتحترم القاعدة حرفيًا.
 *
 * 🔑 **والملف ده بيقرا خانات بس — ما بيفسّرش حاجة.**
 *    كل خانة بتترحّل بقيمتها الخام **و صيغة عرضها**، والسيرفر هو اللي
 *    بيقرر دي تاريخ ولا مدة ولا رقم.
 *    ⛔ **مقصود:** في إكسل التاريخ والمدة **الاتنين** أرقام تسلسلية، واللي
 *       بيفرّق بينهم الصيغة. لو القرار اتاخد هنا، كان بقى فيه **تطبيقان**
 *       لسبع فخاخ متقاسة — والفرق بينهم بيبان في راتب غلط مش في تست أحمر.
 *
 * ⛔ **بلا مكتبة فك ضغط**: المتصفح فيه `DecompressionStream('deflate-raw')`.
 *    إضافة مكتبة معناها اعتمادية جديدة وسلسلة توريد جديدة لحاجة الجهاز
 *    بيعملها أصلًا.
 *
 * ⚠️ الأرقام بتترحّل **نصوصًا** زي ما هي في الملف — `Number()` هنا بيفقد
 *    الدقة، ورقم حساب من ١٩ خانة بيبقى رقم تاني خالص.
 */

/** خانة مرمّزة زي ما `app/db/sheet_grid.decode_cell` بيتوقّعها */
export type XlsxCell =
  | null
  | { s: string }
  | { b: boolean }
  | { n: string; f?: string; fid?: number };

export type XlsxSheet = { name: string; rows: XlsxCell[][] };

export class XlsxError extends Error {
  constructor(public messageAr: string) {
    super(messageAr);
  }
}

/* ═══════════════════════ فك أرشيف zip ═══════════════════════ */

type ZipEntry = {
  name: string;
  method: number;
  offset: number;
  /** ⚠️ **الحجم المضغوط** — الإزاحة ٢٠ في سجل الفهرس، مش ٢٤. */
  csize: number;
  /** الحجم بعد الفك — للتحقق بس */
  usize: number;
};

function u16(v: DataView, o: number) { return v.getUint16(o, true); }
function u32(v: DataView, o: number) { return v.getUint32(o, true); }

/**
 * بيقرا فهرس الأرشيف من آخره (End Of Central Directory).
 * ⚠️ البحث من الآخر للأول لأن الـEOCD ممكن يكون بعده تعليق بطول متغيّر.
 */
function readCentralDirectory(buf: ArrayBuffer): ZipEntry[] {
  const view = new DataView(buf);
  const len = buf.byteLength;
  let eocd = -1;
  const floor = Math.max(0, len - 66_000);
  for (let i = len - 22; i >= floor; i--) {
    if (u32(view, i) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new XlsxError("الملف ده مش إكسل صالح (مش أرشيف).");

  const count = u16(view, eocd + 10);
  let p = u32(view, eocd + 16);
  const out: ZipEntry[] = [];
  const dec = new TextDecoder("utf-8");
  for (let i = 0; i < count; i++) {
    if (u32(view, p) !== 0x02014b50) break;
    const nameLen = u16(view, p + 28);
    const extraLen = u16(view, p + 30);
    const commentLen = u16(view, p + 32);
    out.push({
      name: dec.decode(new Uint8Array(buf, p + 46, nameLen)),
      method: u16(view, p + 10),
      offset: u32(view, p + 42),
      // 🔴 **الإزاحة ٢٠ = المضغوط · ٢٤ = بعد الفك.** خلطهم بيدّي بايتات
      //    زيادة لفاكّ الضغط فبيرمي «Failed to fetch» — وهي رسالة ما
      //    بتقولش إن السبب قراءة حجم غلط. اتمسكت في المتصفح الحقيقي.
      csize: u32(view, p + 20),
      usize: u32(view, p + 24),
    });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
  const DS = (globalThis as { DecompressionStream?: typeof DecompressionStream })
    .DecompressionStream;
  if (!DS) {
    // ⛔ مفيش بديل صامت: المتصفح القديم لازم يعرف إنه هو المشكلة.
    throw new XlsxError(
      "المتصفح ده قديم شوية ومش قادر يفك الملف. جرّب كروم أو إيدج محدّث.",
    );
  }
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DS("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readEntry(buf: ArrayBuffer, e: ZipEntry): Promise<string> {
  const view = new DataView(buf);
  if (u32(view, e.offset) !== 0x04034b50) throw new XlsxError("الأرشيف تالف.");
  const nameLen = u16(view, e.offset + 26);
  const extraLen = u16(view, e.offset + 28);
  const start = e.offset + 30 + nameLen + extraLen;
  const raw = new Uint8Array(buf, start, e.csize);
  const bytes = e.method === 0 ? raw : await inflate(raw);
  if (e.usize && bytes.length !== e.usize) {
    // ⛔ مش تحذير — الجزء الناقص معناه خانات ضايعة بصمت
    throw new XlsxError("الملف اتقرا ناقص — جرّب تحفظه من إكسل تاني.");
  }
  return new TextDecoder("utf-8").decode(bytes);
}

/* ═══════════════════════ قراءة XML ═══════════════════════ */

function parseXml(text: string): Document {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) throw new XlsxError("ملف الإكسل تالف.");
  return doc;
}

/** `"D5"` ⇒ `[5, 4]` (صف، عمود) — الاتنين ١-based زي إكسل */
function cellRef(ref: string): [number, number] {
  let col = 0;
  let i = 0;
  for (; i < ref.length; i++) {
    const c = ref.charCodeAt(i);
    if (c < 65 || c > 90) break;
    col = col * 26 + (c - 64);
  }
  return [Number(ref.slice(i)) || 0, col];
}

/* ═══════════════════════ الواجهة ═══════════════════════ */

/** ⚠️ حد الحجم بيمنع ملف ضخم من تعليق تبويب الموظف قبل ما يبدأ */
const MAX_FILE_BYTES = 20 * 1024 * 1024;

export async function readXlsx(file: File): Promise<XlsxSheet[]> {
  if (file.size > MAX_FILE_BYTES) {
    // ⛔ أرقام لاتينية زي باقي السيستم، **وبالحجم الفعلي**: «أكبر من ٢٠»
    //    ما بتقولش للموظفة تقسّم الملف لكام جزء.
    const mb = Math.round(file.size / (1024 * 1024));
    throw new XlsxError(
      `الملف ${mb} ميجا والحد 20 — ده مش دفتر شهري عادي، قسّمه أو ارفع الشهر المطلوب بس.`,
    );
  }
  const buf = await file.arrayBuffer();
  const entries = readCentralDirectory(buf);
  const byName = new Map(entries.map((e) => [e.name, e]));

  const need = (name: string) => {
    const e = byName.get(name);
    if (!e) throw new XlsxError("الملف ده مش دفتر إكسل (ناقص أجزاء أساسية).");
    return e;
  };

  const wbDoc = parseXml(await readEntry(buf, need("xl/workbook.xml")));
  const relsDoc = parseXml(await readEntry(buf, need("xl/_rels/workbook.xml.rels")));
  const relTarget = new Map<string, string>();
  for (const r of Array.from(relsDoc.getElementsByTagName("Relationship"))) {
    relTarget.set(r.getAttribute("Id") || "", r.getAttribute("Target") || "");
  }

  // النصوص المشتركة — الخانات النصية بتشاور عليها برقم
  const shared: string[] = [];
  const sstEntry = byName.get("xl/sharedStrings.xml");
  if (sstEntry) {
    const sst = parseXml(await readEntry(buf, sstEntry));
    for (const si of Array.from(sst.getElementsByTagName("si"))) {
      // ⚠️ النص ممكن يتقسم على أكتر من `<t>` (تنسيق جوّه الخانة) — لازم يتجمع
      shared.push(Array.from(si.getElementsByTagName("t"))
        .map((t) => t.textContent || "").join(""));
    }
  }

  // 🔑 صيغ الأرقام — دي اللي السيرفر بيفرّق بيها بين التاريخ والمدة
  const numFmtCode = new Map<number, string>();
  const styleFmtId: number[] = [];
  const stylesEntry = byName.get("xl/styles.xml");
  if (stylesEntry) {
    const st = parseXml(await readEntry(buf, stylesEntry));
    for (const f of Array.from(st.getElementsByTagName("numFmt"))) {
      numFmtCode.set(Number(f.getAttribute("numFmtId")), f.getAttribute("formatCode") || "");
    }
    // ⚠️ `cellXfs` بس — `cellStyleXfs` ليها نفس اسم الوسم وترتيبها مختلف،
    //    وخلطهم بيدّي صيغة الخانة الغلط ⇒ ساعات تتقرا تواريخ.
    const cellXfs = st.getElementsByTagName("cellXfs")[0];
    if (cellXfs) {
      for (const xf of Array.from(cellXfs.getElementsByTagName("xf"))) {
        styleFmtId.push(Number(xf.getAttribute("numFmtId") || 0));
      }
    }
  }

  const sheets: XlsxSheet[] = [];
  for (const sh of Array.from(wbDoc.getElementsByTagName("sheet"))) {
    const name = sh.getAttribute("name") || "";
    const rid = sh.getAttribute("r:id") || sh.getAttribute("id") || "";
    let target = relTarget.get(rid) || "";
    if (!target) continue;
    target = target.replace(/^\/?xl\//, "").replace(/^\//, "");
    const entry = byName.get(`xl/${target}`);
    if (!entry) continue;

    const doc = parseXml(await readEntry(buf, entry));
    const rows: XlsxCell[][] = [];
    for (const rowEl of Array.from(doc.getElementsByTagName("row"))) {
      const rowNo = Number(rowEl.getAttribute("r") || 0);
      const line: XlsxCell[] = [];
      for (const c of Array.from(rowEl.getElementsByTagName("c"))) {
        const [, col] = cellRef(c.getAttribute("r") || "");
        if (!col) continue;
        const type = c.getAttribute("t") || "n";
        let cell: XlsxCell = null;

        if (type === "inlineStr") {
          const text = Array.from(c.getElementsByTagName("t"))
            .map((t) => t.textContent || "").join("");
          cell = text ? { s: text } : null;
        } else {
          const v = c.getElementsByTagName("v")[0];
          const raw = v ? (v.textContent || "") : "";
          if (raw !== "") {
            if (type === "s") {
              const text = shared[Number(raw)] ?? "";
              cell = text ? { s: text } : null;
            } else if (type === "str") {
              cell = { s: raw };            // ناتج معادلة نصّي
            } else if (type === "b") {
              cell = { b: raw === "1" };
            } else if (type === "e") {
              // ⚠️ خانة خطأ (`#REF!`) بتترحّل **كنص** عشان تتوسم مش تتبلع
              cell = { s: raw };
            } else {
              const sIdx = Number(c.getAttribute("s") || 0);
              const fid = styleFmtId[sIdx] ?? 0;
              const code = numFmtCode.get(fid);
              cell = code ? { n: raw, f: code, fid } : { n: raw, fid };
            }
          }
        }
        while (line.length < col - 1) line.push(null);
        line[col - 1] = cell;
      }
      while (rows.length < rowNo - 1) rows.push([]);
      rows[rowNo - 1] = line;
    }
    sheets.push({ name, rows });
  }
  if (sheets.length === 0) throw new XlsxError("مفيش ولا تبويبة في الملف ده.");
  return sheets;
}

/** الحمولة اللي بتترفع — الشكل اللي `plan_from_cells` بيتوقّعه بالحرف */
export type FinanceBooks = {
  payments?: XlsxSheet[];
  teacher_payouts?: XlsxSheet[];
  staff_payouts?: XlsxSheet[];
};

export function buildFinancePayload(books: FinanceBooks): unknown {
  const out: Record<string, unknown> = {};
  for (const [k, sheets] of Object.entries(books)) {
    if (sheets && sheets.length) out[k] = { sheets };
  }
  return { kind: "rattel-finance-cells", version: 1, books: out };
}
